import { z } from 'zod';

/**
 * Patient search criteria passed to the EHR connector's real-time lookup.
 *
 * Each field is optional; the connector decides internally which combination is
 * sufficient for its search capability. If the given combination is insufficient
 * (e.g. Office Ally requires at least lastName+DOB), the connector throws a
 * SEARCH_CRITERIA_INSUFFICIENT error rather than returning an empty list —
 * the caller needs to distinguish "no match" from "couldn't search at all."
 */
export const patientSearchCriteriaSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  dateOfBirth: z.string().optional(),
  phone: z.string().optional(),
  memberId: z.string().optional(),
  /** City of residence — used as a weak identity tiebreaker alongside state. */
  city: z.string().optional(),
  /** State of residence (e.g. "CA") — used as a weak identity tiebreaker alongside city. */
  state: z.string().optional(),
});

export type PatientSearchCriteria = z.infer<typeof patientSearchCriteriaSchema>;

export const matchFieldSchema = z.enum([
  'firstName',
  'lastName',
  'dateOfBirth',
  'phone',
  'memberId',
  'city',
  'state',
]);

export type MatchField = z.infer<typeof matchFieldSchema>;

export const patientCandidateSchema = z.object({
  ehrPatientId: z.string(),
  matchConfidence: z.number().min(0).max(1),
  matchedOn: z.array(
    z.object({
      field: matchFieldSchema,
      score: z.number().min(0).max(1),
    })
  ),
  preview: z.object({
    firstName: z.string(),
    lastName: z.string(),
    dateOfBirth: z.string().optional(),
  }),
});

export type PatientCandidate = z.infer<typeof patientCandidateSchema>;

export const searchPatientsResponseSchema = z.object({
  candidates: z.array(patientCandidateSchema),
});

export type SearchPatientsResponse = z.infer<typeof searchPatientsResponseSchema>;
